import React, {useState} from "react";
import MatchScheduler from "../MatchScheduler";
import GroupPhaseResultTable from "./GroupPhaseResultTable";
import GroupPhaseMatchTable from "./GroupPhaseMatchTable";
import MatchResultInput from "../MatchResultInput";
import TournamentSelectionSection from "./TournamentSelectionSection";
import TournamentKnockoutSection from "./TournamentKnockoutSection";
import KnockoutBracket from "./KnockoutBracket";
import {useTournamentPageState} from "./useTournamentPageState";
import {findMatchForTeams, getParticipantName} from "./tournamentViewUtils";
import {isUserAdmin, canExportTournamentParticipants} from "../../utils/userUtils";
import {downloadFileFromApi} from "../../utils/downloadFile";
import "../../styles/GroupPhase.css";

const Tournament = () => {
    const {
        api,
        user,
        tournaments,
        selectedTournamentId,
        selectedTournament,
        tournamentPlayers,
        groups,
        selectedGroupId,
        groupPlayers,
        matches,
        schedulerPair,
        selectedFilterPlayerId,
        editingMatch,
        isTournamentSelectionExpanded,
        knockoutPhaseExists,
        knockoutBracketPlanned,
        activeTournamentView,
        allGroupsCompleted,
        myOverview,
        myOverviewLoading,
        myOverviewError,
        registrationLoading,
        tournamentHasStarted,
        deepLinkMatchId,
        deepLinkGroupId,
        consumeDeepLinkMatch,
        setSelectedGroupId,
        setSchedulerPair,
        setSelectedFilterPlayerId,
        setEditingMatch,
        setIsTournamentSelectionExpanded,
        handleSelectTournament,
        clearTournamentSelection,
        showKnockoutPhase,
        showGroupPhase,
        reloadMatches,
        handleResultChange,
        registerForTournament,
        unregisterFromTournament,
        inviteByEmail,
        initializeKnockoutPhase,
    } = useTournamentPageState();

    const [groupTeamsExpanded, setGroupTeamsExpanded] = useState(false);

    const showingKnockoutPhase = activeTournamentView === "knockout" && (knockoutPhaseExists || knockoutBracketPlanned);

    const resolveParticipantName = (participantOrId) => getParticipantName(participantOrId, groupPlayers, tournamentPlayers);
    const findMatch = (id1, id2) => findMatchForTeams(matches, id1, id2);
    const handleToggleGroup = (groupId) => {
        if (selectedGroupId === groupId) {
            setSelectedGroupId(null);
            setSchedulerPair(null);
            setEditingMatch(null);
            setGroupTeamsExpanded(false);
            return;
        }

        setSelectedGroupId(groupId);
        setSchedulerPair(null);
        setEditingMatch(null);
        // groupTeamsExpanded bleibt unverändert: war es offen, bleibt es offen
    };

    return (
        <div className="groupphase-container">
            <TournamentSelectionSection
                tournaments={tournaments}
                selectedTournamentId={selectedTournamentId}
                isExpanded={isTournamentSelectionExpanded}
                onSelectTournament={handleSelectTournament}
                onResetFilter={() => setSelectedFilterPlayerId("")}
                user={user}
                myOverview={myOverview}
                myOverviewLoading={myOverviewLoading}
                myOverviewError={myOverviewError}
                registrationLoading={registrationLoading}
                onExpand={() => setIsTournamentSelectionExpanded(true)}
                onCollapse={() => setIsTournamentSelectionExpanded(false)}
                onClearTournamentSelection={clearTournamentSelection}
                onRegister={registerForTournament}
                onUnregister={unregisterFromTournament}
                onInviteByEmail={inviteByEmail}
            />
            {selectedTournament && !tournamentHasStarted && (
                <div className="groupphase-section">
                    <h2>Gruppen & Begegnungen</h2>
                    <p className="groupphase-help-text">
                        Die Gruppen und Begegnungen werden erst angezeigt, sobald das Turnier gestartet wurde.
                    </p>
                </div>
            )}

            {/* Gruppenverwaltung nur wenn Turnier gewählt */}
            {selectedTournamentId && tournamentHasStarted && !showingKnockoutPhase && (
                <div className="groupphase-section">
                    <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px"}}>
                        <h2 style={{margin: 0, whiteSpace: "nowrap"}}>Gruppen</h2>
                        <div style={{display: "flex", flexWrap: "wrap", gap: "8px"}}>
                            {groups.map((group) => (
                                <button
                                    key={group.id}
                                    onClick={() => handleToggleGroup(group.id)}
                                    className={`group-button ${
                                        selectedGroupId === group.id ? "group-button-active" : ""
                                    }`}
                                    title={group.name}
                                >
                                    {group.name}
                                    {group.totalMatchCount > 0 && (
                                        <span style={{
                                            marginLeft: 6,
                                            fontSize: "0.8em",
                                            opacity: 0.85,
                                            fontWeight: "normal",
                                        }}>
                                            {group.playedMatchCount}/{group.totalMatchCount}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    {selectedGroupId && groupPlayers.length > 0 && (
                        <div style={{marginTop: 10}}>
                            <div
                                onClick={() => setGroupTeamsExpanded((v) => !v)}
                                style={{cursor: "pointer", userSelect: "none", fontSize: "1.05rem", fontWeight: 600, color: "#1976d2", display: "flex", alignItems: "center", gap: 4}}
                            >
                                <span>{groupTeamsExpanded ? "▴" : "▾"}</span>
                                <span>Teams ({groupPlayers.length})</span>
                            </div>
                            {groupTeamsExpanded && (
                                <GroupTeamList
                                    groupPlayers={groupPlayers}
                                    resolveParticipantName={resolveParticipantName}
                                    onClose={() => setGroupTeamsExpanded(false)}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Begegnungsübersicht */}
            {tournamentHasStarted && !showingKnockoutPhase && selectedGroupId && (
                <div className="groupphase-section">
                    <h2>Begegnungsübersicht</h2>
                    <div style={{marginBottom: "1rem"}}>
                        <label className="groupphase-label">Team filtern:</label>
                        <select
                            value={selectedFilterPlayerId}
                            onChange={(e) => setSelectedFilterPlayerId(e.target.value)}
                            className="groupphase-select"
                            style={{minWidth: 200}}
                        >
                            <option value="">Alle Teams</option>
                            {groupPlayers.map((player) => (
                                <option key={player.id} value={player.id}>
                                    {resolveParticipantName(player.id)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <GroupPhaseMatchTable
                        groupPlayers={groupPlayers}
                        players={groupPlayers}
                        matches={matches}
                        selectedFilterPlayerId={selectedFilterPlayerId}
                        setEditingMatch={setEditingMatch}
                        setSchedulerPair={setSchedulerPair}
                        findMatch={findMatch}
                        reloadMatches={reloadMatches}
                        selectedGroupId={selectedGroupId}
                    />
                </div>
            )}

            {/* Gruppen-Tabelle */}
            {tournamentHasStarted && !showingKnockoutPhase && selectedGroupId && (
                <GroupPhaseResultTable
                    groupId={selectedGroupId}
                    matches={matches}
                    teams={groupPlayers}
                />
            )}

            {/* MatchScheduler für neue Planung einer Paarung */}
            {tournamentHasStarted && !showingKnockoutPhase && selectedGroupId && schedulerPair && (
                <div className="groupphase-overlay">
                    <div className="groupphase-overlay-content">
                        <button
                            className="groupphase-overlay-close"
                            onClick={() => setSchedulerPair(null)}
                            title="Schließen"
                        >
                            &times;
                        </button>
                        <MatchScheduler
                            players={groupPlayers}
                            groupId={selectedGroupId}
                            initialPlayer1={schedulerPair.player1}
                            initialPlayer2={schedulerPair.player2}
                            onScheduled={() => {
                                setSchedulerPair(null);
                                reloadMatches();
                            }}
                            reloadMatches={reloadMatches}
                        />
                    </div>
                </div>
            )}

            {/* Overlay für Ergebnis-Eingabe */}
            {editingMatch && !showingKnockoutPhase && (
                <div className="groupphase-overlay">
                    <div className="groupphase-overlay-content">
                        <button
                            className="groupphase-overlay-close"
                            onClick={() => setEditingMatch(null)}
                            title="Schließen"
                        >
                            &times;
                        </button>
                        <h2>{editingMatch.sets?.length ? "Ergebnis ändern" : "Ergebnis eintragen"}</h2>
                        <div style={{marginBottom: 16}}>
                            <b>{resolveParticipantName(editingMatch.team1 || editingMatch.team1Id)}</b>
                            &nbsp;vs.&nbsp;
                            <b>{resolveParticipantName(editingMatch.team2 || editingMatch.team2Id)}</b>
                        </div>
                        <MatchResultInput
                            match={editingMatch}
                            onSave={async (id, sets, date, court) => {
                                if (id) {
                                    await handleResultChange(id, sets);
                                    setEditingMatch(null);
                                    reloadMatches();
                                } else {
                                    const team1Id = editingMatch.team1?.id ?? editingMatch.team1Id;
                                    const team2Id = editingMatch.team2?.id ?? editingMatch.team2Id;
                                    try {
                                        const newMatch = await api.post("/tournaments/matches", {
                                            groupId: selectedGroupId,
                                            team1Id,
                                            team2Id,
                                            date,
                                            court,
                                        });
                                        await handleResultChange(newMatch.id, sets);
                                        setEditingMatch(null);
                                        reloadMatches();
                                    } catch (error) {
                                        console.error("Fehler beim Erstellen des Matches:", error);
                                        alert("Fehler beim Anlegen des Spiels: " + (error.message || "Unbekannter Fehler"));
                                    }
                                }
                            }}
                        />
                        {editingMatch.id && (isUserAdmin(user) || !editingMatch.startTime || new Date(editingMatch.startTime) >= new Date()) && (
                            <div style={{marginTop: 16, borderTop: "1px solid #eee", paddingTop: 12}}>
                                <button
                                    className="groupphase-button delete-button"
                                    style={{width: "100%"}}
                                    onClick={async () => {
                                        const hasResult = editingMatch.sets?.length > 0;
                                        const msg = hasResult
                                            ? "Soll das Ergebnis dieser Begegnung wirklich gelöscht werden? Die Planung bleibt bestehen."
                                            : "Soll diese geplante Begegnung wirklich gelöscht werden?";
                                        if (!window.confirm(msg)) return;
                                        if (hasResult) {
                                            await api.delete(`/tournaments/matches/${editingMatch.id}/sets`);
                                        } else {
                                            const result = await api.delete(`/tournaments/matches/${editingMatch.id}`);
                                            if (result?.warning) alert(result.warning);
                                        }
                                        setEditingMatch(null);
                                        reloadMatches();
                                    }}
                                >
                                    🗑️ {editingMatch.sets?.length ? "Ergebnis löschen" : "Planung löschen"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {!showingKnockoutPhase && (
                <TournamentKnockoutSection
                    visible={Boolean(selectedTournamentId && tournamentHasStarted)}
                    knockoutPhaseExists={knockoutPhaseExists}
                    bracketPlanned={knockoutBracketPlanned}
                    activeView={activeTournamentView}
                    allGroupsCompleted={allGroupsCompleted}
                    user={user}
                    onInitializeKnockoutPhase={initializeKnockoutPhase}
                    onShowKnockoutPhase={showKnockoutPhase}
                    onShowGroupPhase={showGroupPhase}
                />
            )}

            {showingKnockoutPhase && selectedTournamentId && (
                <KnockoutBracket
                    tournamentId={selectedTournamentId}
                    tournamentName={selectedTournament?.name}
                    user={user}
                    onBackToGroupPhase={showGroupPhase}
                    initialMatchId={deepLinkGroupId == null ? deepLinkMatchId : null}
                    onInitialMatchConsumed={consumeDeepLinkMatch}
                />
            )}

            {selectedTournament && canExportTournamentParticipants(user) && (
                <div className="groupphase-section">
                    <button
                        className="groupphase-button secondary"
                        disabled={tournamentPlayers.length === 0}
                        onClick={() => downloadFileFromApi(
                            api,
                            `/tournaments/${selectedTournament.id}/export/players.pdf`,
                            `teilnehmerliste-${selectedTournament.id}.pdf`
                        )}
                    >
                        Teilnehmerliste als PDF
                    </button>
                </div>
            )}
        </div>
    );
};

const GroupTeamList = ({groupPlayers, resolveParticipantName, onClose}) => {
    const [expandedTeams, setExpandedTeams] = useState(new Set());

    const toggleTeam = (teamId) => {
        setExpandedTeams((prev) => {
            const next = new Set(prev);
            if (next.has(teamId)) next.delete(teamId);
            else next.add(teamId);
            return next;
        });
    };

    return (
        <ul className="groupphase-list groupphase-team-list">
            {groupPlayers.map((team) => {
                const isMultiPlayer = (team.players?.length ?? 0) > 1;
                const isExpanded = expandedTeams.has(team.id);
                return (
                    <li key={team.id} className="groupphase-team-item">
                        <div
                            className={`groupphase-team-header${isMultiPlayer ? " groupphase-team-header--toggleable" : ""}`}
                            onClick={isMultiPlayer ? () => toggleTeam(team.id) : () => onClose?.()}
                        >
                            <span>{resolveParticipantName(team)}</span>
                            {isMultiPlayer && (
                                <span className="groupphase-team-toggle">{isExpanded ? "▴" : "▾"}</span>
                            )}
                        </div>
                        {isMultiPlayer && (
                            <ul className={`groupphase-player-sublist${isExpanded ? " groupphase-player-sublist--open" : ""}`}>
                                {team.players.map((player) => (
                                    <li key={player.id} onClick={() => onClose?.()} style={{cursor: "pointer"}}>{player.name}</li>
                                ))}
                            </ul>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default Tournament;
